import { SortModel, SortModelQuery } from '../DataTableQuery.types';
import { useDataTableContext } from '../../DataTableContext';
import { useDeepCompare, useDeepCompareWithPrevious } from '../../../../../hooks';
import { useEffect, useMemo } from 'react';
import { filterNotEmpty } from '../../../../../utils/listHelpers';

export function useSortChanges(
  applyChanges: (nextModel: SortModelQuery<SortModel>[] | undefined, persist?: boolean) => void,
  enabled: boolean
) {
  const { getAllColumns } = useDataTableContext();
  const columns = getAllColumns?.();
  const columnsMemoTrigger = useDeepCompare(columns?.map((column) => column.getIsSorted()) ?? []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  const sortedColumns = useMemo(
    () =>
      columns
        ?.filter((column) => column.getIsSorted())
        .map(({ id, getIsSorted }) => ({ type: id, reversed: getIsSorted() === 'desc' })) ?? [],
    // We need columnsMemoTrigger because columns object don't trigger re-render
    [columns, columnsMemoTrigger]
  );

  const [sortHasChanged, prevColumns] = useDeepCompareWithPrevious(sortedColumns);
  useEffect(() => {
    if (!enabled) return;

    const shouldUpdateSortQuery = sortHasChanged && prevColumns;
    if (shouldUpdateSortQuery) {
      const [firstColumn] = sortedColumns;
      if (!firstColumn) {
        setTimeout(() => {
          applyChanges(undefined, true);
        }, 0);
        return;
      }
      const { type, reversed } = firstColumn;
      const typeSafe = type as SortModel;
      const data = typeof reversed === 'boolean' ? { type: typeSafe, reversed } : undefined;
      setTimeout(() => {
        applyChanges(filterNotEmpty([data]), true);
      }, 0);
    }
  }, [sortHasChanged, sortedColumns, prevColumns, enabled, applyChanges]);
}
